import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { ResultMessageType } from "@multiweb/types/contracts";
import { Event } from "../../../../Global/Base/Events";
import { InvalidArgumentException } from "../../../../Global/Base/Exceptions";
import { IValidationError, ValidationErrorType } from "../../../../OW.Remoting/Basic/EOWExceptionsInterfaces";
import { IValidationDataObject, IValidationDataSimple } from "../../../../OW.Remoting/Remoting/DataTypes/IValidationDataObject";
import { IValidatable, IValidatableContainer } from "../../../UI.BaseControls/ControlInterfaces";
import { DataRow } from "../../../UI.Controls/DataGridSubcontrols/Components/DataRow";
import { IAttributeControl } from "../Controls/ControlInterfaces";

/**
 * Pomocny objekt sdruzujici validacni chybu a komponenty, ke kterym se vaze
 */
export interface IValidationData extends IValidationDataSimple {
    components: IValidatableComponentData[];
}

/**
 * Pomocny objekt pro data komponenty, ktera jsou potreba pro validaci
 */
export interface IValidatableComponentData {
    control: IValidatable;
    container?: IValidatableContainer;
    attributeName: string;
    showsInvalidData: () => boolean;
}

const logger = LazyLoadingSectionLogger.create(LoggingSection.validation);

/**
 * Pomocný objekt obsahující informace k validaci objektu
 */
export interface IValidationMessage {
    /**
     * Zpráva o výsledku validity objektu
     */
    readonly message: string;
    /**
     * Typ zprávy
     */
    readonly type: ResultMessageType;
    /**
     * Doplňující informace k validitě objektu. Může obsahovat např. seznam validovaných komponent aj.
     */
    readonly additionalParams: string[];
}

/**
 * Pomocný objekt sdružující informace o validitě objektu a daty potřebnými pro zobrazení v aplikaci.
 */
export interface IValidationResult {
    /**
     * Viz {@link IValidationDataObject}
     */
    readonly data: IValidationDataObject<any>;
    /**
     * Viz {@link IValidationMessage}
     */
    readonly messages: IValidationMessage[];
}

/**
 * Pomocný objekt obsahující výsledek jednoho cyklu validace.
 * Typicky se používá při validaci práv a umožňuje uložení výsledků validace.
 */
export interface IValidationOverview {
    /**
     * Viz {@link IValidationResult}
     */
    readonly validationMessages: IValidationResult[];
    /**
     * Výsledek validace všech potřebných komponent na framu.
     */
    readonly ownerValidated: boolean;
    /**
     * Příznak, zda existuje nějaká validační chyba.
     */
    readonly hasErrors?: boolean;
}

/**
 * Manager spravuje validační chyby vzniklé při ukládádní záznamů - nastavuje a odebírá je souvisejícím komponentám
 */
export class ValidationManager {
    private _invalidObjects = new Map<any, IValidationDataObject<IValidationData>>();

    /**
     * Událost, ktera se vyvola při změně validnosti sledovaného objektu
     */
    public objectValidityChange = new Event<ValidationManager, { object: IValidationDataObject<IValidationData>; validityState: boolean }>();

    /**
     * Seznam funkci události pro možnost odstranění jednotlivých zaregistrovanych udalosti
     */
    private _objectValidityChangeMethodMap = new Map<string, (object: IValidationData, validityState: boolean) => void>();

    /**
     * Seznam řádků, které mají být zobrazeny jako nevalidní (např. po UserException)
     */
    private _invalidRows = new Map<string, DataRow>();

    /**
     * Zda existuje nějaká validační chyba (recommended a postponed není validační chyba, záznam lze uložit i když není toto pole vyplněné)
     */
    public get hasErrors(): boolean {
        for (const object of this._invalidObjects.values()) {
            const objectMessages = object.getValidationErrors();
            for (const msg of objectMessages)
                if (msg.error.type === ValidationErrorType.dataError ||
                    msg.error.type === ValidationErrorType.inputError ||
                    msg.error.type === ValidationErrorType.requiredError)
                    return true;
        }
        return false;
    }

    /**
     * Zaregistruje a zobrazi validacni chyby vznikle na zaklade dat objektu na prislusnych komponentach
     * @param dataObject datovy objekt, ke kteremu se vztahuje validacni chyba
     * @param error validacni chyba
     * @param components kolekce komponent s chybami a pridruzenymi informacemi
     */
    public addDataError(dataObject: IValidationDataObject<IValidationData>, error: IValidationError, components: IValidatableComponentData[]): void {
        if (!dataObject || !error || !components)
            throw new InvalidArgumentException();

        logger.debug("addDataError", dataObject, error);
        const validationObject = this._getOrAddValidationObject(dataObject);

        // Zkontroluje, jestli již neobsahuje validační chybu typu postponedError z tohoto atributu, aby nevznikla duplicita
        if (error.type === ValidationErrorType.postponedError) {
            for (let i = 0; i < validationObject.getValidationErrors().length; i++) {
                for (let j = 0; j < validationObject.getValidationErrors()[i].error.attributeNames.length; j++) {
                    if (error.attributeNames.some(errorAttrName => errorAttrName === validationObject.getValidationErrors()[i].error.attributeNames[j]))
                        return;
                }
            }
        }

        // predejde pridavani duplicit z ruznych framu - pouze se zmerguji komponenty patrici k datovemu erroru
        const existingError = validationObject.getValidationErrors().find((e) => e.error === error); // chyb k jednomu objektu budou radove jednotky, takze by tohle nemel byt problem
        if (existingError) {
            for (let i = 0; i < components.length; i++) {
                if (existingError.components.some(c => c.attributeName !== components[i].attributeName))
                    existingError.components = existingError.components.concat(components);
            }
        } else {
            validationObject.addError({ error: error, components: components });
        }

        ValidationManager.invalidateComponents(components, error);
        logger.debug("addDataError", this._objectValidityChangeMethodMap);
    }

    /**
     * Zaregistruje validacni chybu (uz se nenastavuje komponente)
     * @param dataObject datovy objekt, ke kteremu se vztahuje validacni chyba
     * @param error validacni chyba
     * @param component komponenta, na ktere vznikla validacni chyba a pridruzene informace
     */
    public addComponentError(dataObject: IValidationDataObject<IValidationData>, error: IValidationError, component: IValidatableComponentData): void {
        if (!dataObject || !error || !component)
            throw new InvalidArgumentException();

        logger.debug("addComponentError", dataObject, error);
        const validationObject = this._getOrAddValidationObject(dataObject);

        // predejde pridavani duplicit
        if (validationObject.getValidationErrors().some((e) => e.error.type === error.type && e.components.length === 1 && e.components[0].control === component.control))
            return;

        error.attributeNames = [component.attributeName];
        validationObject.addError({ error: error, components: [component] });
        logger.debug("addComponentError", this._objectValidityChangeMethodMap);

        //if (component.container)
        //    component.container.isValid = false;
    }

    /**
     * Přidá do kolekce řádek, který má být zobrazen jako nevalidní (např. po UserException)
     * @param id záznamu
     * @param row řádek seznamu
     */
    public addRowError(id: string, row: DataRow): void {
        if (!id || !row) {
            throw new InvalidArgumentException();
        }

        row.isValid = false;

        this._invalidRows.set(id, row);
    }

    /**
     * Ziska datovy objekt ze seznamu zaregistrovanych a pokud tam takovy neexistuje, tak ho tam prida
     */
    private _getOrAddValidationObject(dataObject: IValidationDataObject<IValidationData>): IValidationDataObject<IValidationData> {
        const id = dataObject.id;

        let validationObject = this._invalidObjects.get(id.toString());

        // pri prvni chybe se objekt prida do kolekce nevalidnich objektu
        if (validationObject == null) {
            validationObject = dataObject;
            this._invalidObjects.set(id.toString(), validationObject);
            // zaregistrovani udalosti pro objekt

            let fnc: (object: IValidationData, validityState: boolean) => void;

            validationObject.validityChange.add(fnc = (_object, validityState) => {
                this.objectValidityChange.trigger(this, {
                    object: dataObject,
                    validityState: validityState,
                });
            });

            this._objectValidityChangeMethodMap.set(dataObject.id, fnc);
        }

        return validationObject;
    }

    /**
     * Odstraní všechny validační chyby ze všech zaznamů
     * @param id Pokud je uvedeno id, tak smaže jenom z tohoto konkrétního záznamu
     * @param errorType Pokud je uveden errorType, tak smaže jenom tento typ chyby
     */
    public clearErrors(id: any = null, errorType?: ValidationErrorType): void {
        logger.debug("clearErrors", id);
        for (const [objectId, object] of this._invalidObjects.entries()) {
            // ReSharper disable once CoercedEqualsUsing
            if (id != null && id != objectId)
                continue;

            for (const errorData of object.getValidationErrors()) {
                if (!errorType || errorType === errorData.error.type) {
                    //Pokud komponenta není validní, tak ji zvalidním
                    errorData.components.forEach(comp => {
                        if (comp.control && !comp.control.isValid)
                            comp.control.setValid();
                    });

                    errorData.components = [];
                    object.removeError(errorData);
                }
            }

            // pokud už zadna chyba není zobrazena nad žádnou komponentou, tak se rovnou odstraňuje kolekce chyb zaznamu
            if (object.isValid) {
                this._removeInvalidObject(object);
            }
        }

        // část pro zvalidnění řádků seznamu
        const validateAndRemoveFnc = (id: string, row: DataRow): void => {
            row.isValid = true;

            this._invalidRows.delete(id);
        };

        if (id != null) {
            const row = this._invalidRows.get(id);

            if (row) {
                validateAndRemoveFnc(id, row);
            }
        } else {
            this._invalidRows.forEach((value, key) => {
                validateAndRemoveFnc(key, value);
            });
        }

        logger.debug("clearErrors", this._objectValidityChangeMethodMap);
    }

    /**
     * Odstraní validační chyby z pole @param ids
     * Modifikovaná metoda @function clearErrors
     * @param ids pole id objektů u kterých je potřeba odstranit validační chyby
     * @param errorType Pokud je uveden errorType, tak smaže jenom tento typ chyby
    */
    public clearMultipleErrors(ids: string[] = [], errorType?: ValidationErrorType): void {
        logger.debug("clearMultipleErrors", ids);

        for (const id of ids) {
            const object = this._invalidObjects.get(id);
            if (object) {
                for (const errorData of object.getValidationErrors()) {
                    if (!errorType || errorType === errorData.error.type) {
                        //Pokud komponenta není validní, tak ji zvalidním
                        errorData.components.forEach(comp => {
                            if (comp.control && !comp.control.isValid) {
                                comp.control.setValid();
                            }
                        });
                        errorData.components = [];
                        object.removeError(errorData);
                    }
                }
                if (object.isValid) {
                    this._removeInvalidObject(object);
                }

                const row = this._invalidRows.get(id);

                if (row) {
                    row.isValid = true;
                    this._invalidRows.delete(id);
                }
            }
        }
        logger.debug("clearMultipleErrors", this._objectValidityChangeMethodMap);
    }

    /**
     * Odstraní validační chyby z daného atributu daného záznamu
     * @param id id datoveho objektu
     * @param attributeName název atributu, pro který se odstraňují chyby
     * @param isLinkedAttribute udává, zda se jedná o provázaný atribut
     */
    public removeDataErrors(id: any, attributeName: string, isLinkedAttribute: boolean = false): void {
        if (!id)
            throw new InvalidArgumentException();

        logger.debug("removeDataErrors", id, attributeName);

        attributeName = attributeName != null ? attributeName.toUpperCase() : null;

        const object = this._invalidObjects.get(id.toString());
        if (object == null)
            return;

        //const containerErrors = new Map<IValidatableContainer, boolean>();
        const validationErrors = object.getValidationErrors();
        logger.debug("removeDataErrors-start", this._objectValidityChangeMethodMap);

        logger.groupCollapsed("removeDataErrors-for");
        const linkedAttributes: string[] = [];
        for (const errorData of validationErrors) {
            logger.group("errorData");
            const errorRemovedFromLinkedAttr = errorData.error.linkedAttributes && errorData.error.attributeNames.some((c) => c != null && c.toUpperCase() === attributeName);
            if (errorRemovedFromLinkedAttr)
                linkedAttributes.push(...errorData.error.attributeNames.filter((c) => c.toUpperCase() !== attributeName && !linkedAttributes.includes(c)));
            //Musim si udelat kopii, protoze v removeComponentErrors bych se to menilo pod rukama
            const components = errorData.components.slice(0);
            // projde vsechny komponenty prirazene k chybe a na prislusnych zrusi nevalidnost
            for (let i = 0; i < components.length; ++i) {
                logger.group("components");
                const data = components[i];

                // pokud komponenta právě nezobrazuje požadovaný záznam
                if (!data.showsInvalidData()) {
                    logger.debug("rde-vetev-1");
                    // tak ji není potřeba revalidovat, ale chybu je stále potřeba smazat
                    components.splice(i--, 1);
                    continue;
                }

                // kontejner se prida do kolekce k pozdejsimu zpracovani
                //if (data.container != null && !containerErrors.has(data.container))
                //    containerErrors.set(data.container, false);

                // chyba se odstrani v nasledujicich pripadech:
                // 1) chyba neni pripojena k atributu - odstranuje se pri jakekoliv zmene jakehokoliv atributu (a bude se jednat o kontejner)
                // 2) atributy jsou linked - validační hláška se odstraní ze všech komponent pri odstraneni z jednoho z nich
                // 3) komponenta zobrazuje prave zmeneny atribut
                if (data.attributeName == null || errorRemovedFromLinkedAttr || data.attributeName.toUpperCase() === attributeName) {
                    // -> odstraneni chyby
                    logger.debug("rde-vetev-2", data.attributeName, errorRemovedFromLinkedAttr, attributeName);
                    // spusti validaci na komponente - pokud byl stav nevalidnosti nastaven z venku, tak se bezne odstrani - zustane pripadna klientska nevalidnost
                    if (data.control != null) {
                        logger.debug("rde-vetev-2-1");

                        if (!data.control.isValid && isLinkedAttribute) {
                            // pokud se jedná o provázaný atribut, tak musíme odstranit chyby i z komponenty navázané na něho
                            this.clearErrors(object.id, errorData.error.type);
                        }

                        data.control.validate();

                        // pokud komponenta zustala nevalidni (klientskou validacni chybou)
                        if (!data.control.isValid) {
                            logger.debug("rde-vetev-2-1-1");
                            //if (data.container != null)
                            //    containerErrors.set(data.container, true);
                            continue;
                        }
                    }

                    const position = errorData.components.indexOf(data);
                    if (position > -1) {
                        logger.debug("rde-vetev-2-2");
                        errorData.components.splice(position, 1);
                    }
                    continue;
                }

                // -> chyba zustava a je potreba ji zachovat i na kontejneru
                //if (data.container != null)
                //    containerErrors.set(data.container, true);
                logger.groupEnd();
            }

            if (errorData.components.length === 0 && !(attributeName == null && errorData.error.keepOnAttributeChange)) {
                logger.debug("rde-vetev-3", attributeName, errorData.error.keepOnAttributeChange);
                object.removeError(errorData);
            }
            logger.groupEnd();
        }
        logger.groupEnd();

        // projdou se kontejnery, a ty, ktere nemaji nevalidni komponenty, se zvalidni
        //for (let [container, hasErrors] of containerErrors.entries()) {
        //    if (!hasErrors)
        //        container.isValid = true;
        //}

        // pokud už zadna chyba není zobrazena nad žádnou komponentou, tak se rovnou odstraňuje kolekce chyb zaznamu
        if (object.isValid) {
            this._removeInvalidObject(object);
        }

        logger.debug("Recursively removing dataErrors from linked attributes: ", linkedAttributes);
        for (const attribute of linkedAttributes)
            this.removeDataErrors(id, attribute, true);

        logger.debug("removeDataErrors-end", this._objectValidityChangeMethodMap);
    }

    /**
     * Odstrani validacni chyby z dane komponenty zobrazujici dany zaznam,
     * u komponenty se predpoklada, ze uz je ve validnim stavu, uz se nevola revalidace
     * @param id id datoveho objektu
     * @param component komponenta, ze ktere se ma odregistrovat validacni chyba
     * @param errorType nepovinný typ chyb, které se mají odstraňovat - pokud není uveden, tak se odstraňují všechny typy
     */
    public removeComponentErrors(id: any, component: IValidatable, errorType?: ValidationErrorType): void {
        if (!id || !component)
            throw new InvalidArgumentException();

        logger.debug("removeComponentErrors", id, component);

        const object = this._invalidObjects.get(id.toString());
        if (object == null)
            return;

        const errors = object.getValidationErrors();
        for (const errorData of errors) {
            // pokud se mají odstraňovat pouze errory určitého typu
            if (errorType != null && errorType !== errorData.error.type)
                continue;

            for (let i = 0; i < errorData.components.length; ++i) {
                const componentData = errorData.components[i];
                if (component !== componentData.control)
                    continue;

                errorData.components.splice(i, 1);
                if (errorData.components.length === 0)
                    object.removeError(errorData);

                //TODO: bude jeste potreba domyslet a dodelat zvalidnovani kontejneru, ktere uz nemaji zadnou chybu, i v removeDataErrors je to dost divoky kod a timhle se to jeste zeslozitilo
                break;
            }
        }

        // pokud už zadna chyba není zobrazena nad žádnou komponentou, tak se rovnou odstraňuje kolekce chyb zaznamu
        if (object.isValid) {
            this._removeInvalidObject(object);
        }
        logger.debug("removeComponentErrors-end", this._objectValidityChangeMethodMap);
    }

    /**
     * Odstraní objekt z kolekce nevalidních záznamů.
     * @param object Objekt k odstranění.
     */
    private _removeInvalidObject(object: IValidationDataObject<IValidationData>): void {
        if (!object)
            return;

        this._invalidObjects.delete(object.id.toString());

        // odstraneni navazanych udalosti
        object.validityChange.remove(this._objectValidityChangeMethodMap.get(object.id));
        this._objectValidityChangeMethodMap.delete(object.id);
    }

    /**
     * Zkontroluje, zda existují nějaké chybové hlášky pro daný záznam a pokud ano, tak je nastaví příslušným komponentám
     * @param id id datoveho objektu
     */
    public checkDataErrors(id: any): void {
        if (!id)
            throw new InvalidArgumentException();

        this._checkErrors(id);
    }

    public checkComponentErrors(id: any, component: IValidatable): void {
        if (!id || !component)
            throw new InvalidArgumentException();

        this._checkErrors(id, component);
    }

    private _checkErrors(id: any, component: IValidatable = null): void {
        const object = this._invalidObjects.get(id.toString());
        if (object == null)
            return;

        const errors = object.getValidationErrors();
        for (const errorData of errors) {
            for (const componentData of errorData.components) {
                // pokud se maji zobrazit pouze chyby na komponente, tak se ostatni komponenty preskakuji
                if (component && component !== componentData.control)
                    continue;

                ValidationManager._invalidateComponent(componentData, errorData.error);

                if (component)
                    break;
            }
        }
    }

    /**
     * Znevalidní předané komponenty tím, že jim nastaví zadanou chybu.
     * @param components Seznam komponent ke znevalidnění
     * @param error Chyba, která se nastaví do komponent.
     */
    public static invalidateComponents(components: IValidatableComponentData[], error: IValidationError): void {
        for (const data of components)
            ValidationManager._invalidateComponent(data, error);
    }

    /**
     * Znevalidní předanou komponentu a jako chybu ji nastaví zadanou chybu.
     * @param componentData Komponenta a přidružená data.
     * @param error Chyba, která se nastaví do komponenty.
     */
    private static _invalidateComponent(componentData: IValidatableComponentData, error: IValidationError): void {
        // a ty, které prave zobrazuji chybny zaznam, znevalidni
        if (!componentData.showsInvalidData())
            return;

        if (componentData.control != null && componentData.control.isValid) {
            // pokud se validacni chyba tyka spatneho uzivatelkeho vstupu, tak je treba tuto nevalidni hodnotu prenaset
            if (error.type === ValidationErrorType.inputError && componentData.control instanceof IAttributeControl)
                componentData.control.displayedValue = error.invalidValue;

            componentData.control.setInvalid(null, error.errorMessage, error.type);
        }
    }

    /**
     * Vratí všechny validační chyby spolu s daty potřebnými pro zobrazení.
     */
    public getErrorMessages(serverValidationOnly?: boolean): IValidationResult[] {
        const result: IValidationResult[] = [];

        for (const object of this._invalidObjects.values()) {
            const objectMessages = object.getValidationErrors();

            const messages: IValidationMessage[] = [];

            for (const objectMessage of objectMessages) {
                if (serverValidationOnly && objectMessage.error.type !== ValidationErrorType.dataError) {
                    continue;
                }

                const errorType = objectMessage.error.type === ValidationErrorType.recommendedError ? ResultMessageType.recommendation : ResultMessageType.error;
                const message: IValidationMessage = {
                    message: objectMessage.error.errorMessage,
                    type: errorType,
                    additionalParams: objectMessage.error.componentNames || [],
                };

                messages.push(message);
            }

            result.push({ data: object, messages: messages });
        }

        return result;
    }
}
